import { Action, ActionCreator } from "typescript-fsa";
import log from "loglevel";
import moment from "moment";
import { trimStart, defaults, transform, isArray, snakeCase } from "lodash";
import { Moment } from "moment";

import { apiRequest } from "./actions";
import { RequestLogin } from "./models";
import { ApiOptions } from "./models/actions";
import { ApiRequest } from "./requests";
import * as auth from "@auth/actions";
import * as charts from "@charts/actions";
import * as deliveries from "@deliveries/actions";
import * as dsps from "@dsps/actions";
import * as localisation from "@localisation/actions";
import * as oldwata from "@oldwata/actions";
import * as music from "@music/actions";
import * as reports from "@reports/actions";
import * as sales from "@sales/actions";
import * as search from "@search/actions";
import * as upload from "@upload/actions";
import * as vendor from "@vendor/actions";
import { IUser } from "@auth/models";
import { IReport } from "@reports/models";

const snakeCasise = (obj: any) => transform(obj, (acc: any, value, key, target) => {
    const snakeKey = isArray(target) ? key : snakeCase(String(key));
    acc[snakeKey] = value;
    return acc;
});

interface DatePeriodGetOptions {
    from?: Date;
    to?: Date;
    download?: boolean;
}

interface TrendsDataGetOptions extends DatePeriodGetOptions {
    albumId?: number;
    trackId?: number;
    artistId?: number;
}

interface ChartsDataGetOptions extends DatePeriodGetOptions {
    name?: string;
}

interface DemographicsGetOptions extends TrendsDataGetOptions {
    type?: "gender" | "age" | "region";
}

interface PaginatedTrendsGetOptions extends TrendsDataGetOptions {
    page?: number;
}

const isTrendsGetOptions = (params: DatePeriodGetOptions): params is TrendsDataGetOptions => {
    return (params as DatePeriodGetOptions).from !== undefined &&
        (params as TrendsDataGetOptions).albumId !== undefined;
};

const isPaginatedTrendsGetOptions = (params: DatePeriodGetOptions): params is PaginatedTrendsGetOptions => {
    return (params as DatePeriodGetOptions).from !== undefined &&
        (params as PaginatedTrendsGetOptions).page !== undefined;
};

const isChartGetOptions = (params: DatePeriodGetOptions): params is ChartsDataGetOptions => {
    return (params as DatePeriodGetOptions).from !== undefined &&
        (params as ChartsDataGetOptions).name !== undefined;
};

const defaultDatePeriodGetOptions = (params: DatePeriodGetOptions) => {
    return defaults(params, {
        download: false,
    } as DatePeriodGetOptions);
};

const defaultChartsDataGetOptions = (params: ChartsDataGetOptions) => {
    return defaults(params, {
        download: false,
    } as ChartsDataGetOptions);
};

const defaultTrendsDataGetOptions = (params: TrendsDataGetOptions) => {
    return defaults(params, {
        from: null,
        to: null,
        download: false,
        albumId: null,
        trackId: null,
        artistId: null,
    } as TrendsDataGetOptions);
};

const buildDatePeriodUrl = (prefix: string, params: TrendsDataGetOptions | ChartsDataGetOptions) => {
    const { from, to, download } = params;
    const queryStrings: string[] = [];
    if (from != null) {
        queryStrings.push("from=" + moment(from).format("YYYY-MM-DD"));
    }
    if (to != null) {
        queryStrings.push("to=" + moment(to).format("YYYY-MM-DD"));
    }
    if (isTrendsGetOptions(params) && params.albumId != null) {
        queryStrings.push("album_id=" + params.albumId);
    }
    if (isTrendsGetOptions(params) && params.trackId != null) {
        queryStrings.push("track_id=" + params.trackId);
    }
    if (isTrendsGetOptions(params) && params.artistId != null) {
        queryStrings.push("artist_id=" + params.artistId);
    }
    if (isPaginatedTrendsGetOptions(params) && params.page != null) {
        queryStrings.push("page=" + params.page);
    }
    if (isChartGetOptions(params) && params.name != null) {
        queryStrings.push("name=" + params.name);
    }
    const queryString = (queryStrings.length > 0 ? "?" : "") + queryStrings.join("&");
    return prefix + (download ? "/download" : "") + queryString;
};

// Functions to call the api.salesGet function for an album or track.
type TrendsDataGetFn = (params: TrendsDataGetOptions) => Action<ApiRequest>;

const makeTrendsDataGetAlbum = (fn: TrendsDataGetFn, albumId: number) =>
    (params: TrendsDataGetOptions) => {
        return fn({...params, albumId});
    };
const makeTrendsDataGetTrack = (fn: TrendsDataGetFn, trackId: number) =>
    (params: TrendsDataGetOptions) => {
        return fn({...params, trackId});
    };
const makeTrendsDataGetArtist = (fn: TrendsDataGetFn, artistId: number) =>
    (params: TrendsDataGetOptions) => {
        return fn({...params, artistId});
    };

const makeTrendsDataGet = (fn: TrendsDataGetFn, params: TrendsDataGetOptions) => {
    const { albumId, trackId, artistId } = params;
    let actionCreator = fn;
    if (albumId != null) {
        actionCreator = makeTrendsDataGetAlbum(fn, albumId);
    }
    else if (trackId != null) {
        actionCreator = makeTrendsDataGetTrack(fn, trackId);
    }
    else if (artistId != null) {
        actionCreator = makeTrendsDataGetArtist(fn, artistId);
    }

    return () => actionCreator(params);
};

export const makeSalesGet = (
    albumId: number,
    trackId: number,
    artistId: number,
    from: Moment,
    to: Moment,
    download: boolean = false,
) => {
    return makeTrendsDataGet(Api.salesGet, {
        from: from.toDate(),
        to: to.toDate(),
        albumId,
        trackId,
        artistId,
        download,
    });
};

export const makeDemographicsGet = (
    albumId: number,
    trackId: number,
    artistId: number,
    from: Moment,
    to: Moment,
    download: boolean = false,
) => {
    return makeTrendsDataGet(Api.demographicsGet, {
        from: from.toDate(),
        to: to.toDate(),
        albumId,
        trackId,
        artistId,
        download,
    });
};

export const makeHeadlineStatsGet = (
    albumId: number,
    trackId: number,
    artistId: number,
    from: Moment,
    to: Moment,
    download: boolean = false,
) => {
    return makeTrendsDataGet(Api.headlineStatsGet, {
        from: from.toDate(),
        to: to.toDate(),
        albumId,
        trackId,
        artistId,
        download,
    });
};

export const makePlaylistTrendsGet = (
    albumId: number,
    trackId: number,
    artistId: number,
    from: Moment,
    to: Moment,
    download: boolean = false,
    page: number = null,
) => {
    return makeTrendsDataGet(Api.playlistsTrends, {
        from: from.toDate(),
        to: to.toDate(),
        albumId,
        trackId,
        artistId,
        download,
        page,
    } as PaginatedTrendsGetOptions);
};

export const makeDemographicsDownloadGet = (
    albumId: number,
    trackId: number,
    artistId: number,
    from: Moment,
    to: Moment,
    type: "gender" | "age" | "region" = null,
) => {
    return () => Api.demographicsGet({
        from: from.toDate(),
        to: to.toDate(),
        albumId,
        trackId,
        artistId,
        download: true,
        type,
    });
};

export const makePlaylistsShow = (id: number, from: Moment, to: Moment, download: boolean = false) => () => {
    return Api.playlistsShow(id, {
        from: from.toDate(),
        to: to.toDate(),
        download,
    });
};

export const makeChartsIndex = (
    from: Moment,
    to: Moment,
    download: boolean = false,
    name: string = undefined,
) => () => {
    return Api.chartsIndex({
        from: from.toDate(),
        to: to.toDate(),
        download,
        name,
    });
};

export class Api {
    /**
     * Get the action to perform a login request.
     *
     * @param credentials RequestLogin
     * @return Action<ApiRequest>
     */
    public static login(credentials: RequestLogin): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "login",
            method: "POST",
            secured: false,
            types: [
                auth.requestLogin,
                auth.loginSuccess,
                auth.loginFailure,
            ],
            body: credentials,
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting login for user " + credentials.email);
        return apiRequest(request);
    }

    /**
     * Get the old Wata url for the given path.
     *
     * @param path string
     * @return Action<ApiRequest>
     */
    public static oldwataRoute(path: string)
    {
        const options: ApiOptions = {
            endpoint: "oldwata/route?path=" + encodeURIComponent(path),
            method: "GET",
            secured: true,
            types: [
                oldwata.requestRoute,
                oldwata.routeSuccess,
                oldwata.routeFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting oldwata route request with path '" + path + "'");
        return apiRequest(request);
    }

    // Countries
    /**
     * Get the action to perform a reports index request.
     *
     * @return Action<ApiRequest>
     */
    public static countriesIndex(): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "countries",
            method: "GET",
            secured: true,
            types: [
                localisation.requestCountriesIndex,
                localisation.countriesIndexSuccess,
                localisation.countriesIndexFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting countries index request");
        return apiRequest(request);
    }

    // Reports
    /**
     * Get the action to perform a reports index request.
     *
     * @return Action<ApiRequest>
     */
    public static reportTypesIndex(): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "report-types",
            method: "GET",
            secured: true,
            types: [
                reports.requestReportTypesIndex,
                reports.reportTypesIndexSuccess,
                reports.reportTypesIndexFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting report types index request");
        return apiRequest(request);
    }

    /**
     * Get the action to perform a reports index request.
     *
     * @return Action<ApiRequest>
     */
    public static reportsIndex(page: number = 1, filters: {
        typeIds?: number[],
    } = {}): Action<ApiRequest> {
        const queryStrings: string[] = [];
        if (page > 1) {
            queryStrings.push("page=" + page);
        }
        if (filters.typeIds != null) {
            for (const typeId of filters.typeIds) {
                queryStrings.push("type_ids[]=" + typeId);
            }
        }
        const queryString = (queryStrings.length > 0 ? "?" : "") + queryStrings.join("&");
        const options: ApiOptions = {
            endpoint: "reports" + queryString,
            method: "GET",
            secured: true,
            types: [
                reports.requestReportsIndex,
                reports.reportsIndexSuccess,
                reports.reportsIndexFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting reports index request, page " + page);
        return apiRequest(request);
    }

    /**
     * Get the action to perform a reports store request.
     *
     * @return Action<ApiRequest>
     */
    // TODO actually pass through the report to store.
    public static reportsStore(report: IReport): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "reports",
            method: "POST",
            secured: true,
            types: [
                reports.requestReportsStore,
                reports.reportsStoreSuccess,
                reports.reportsStoreFailure,
            ],
            body: snakeCasise(report),
    };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting reports store request");
        return apiRequest(request);
    }

    /**
     * Get the action to perform a reports show request.
     *
     * @param id number
     * @return Action<ApiRequest>
     */
    public static reportsShow(id: number): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "reports/" + id,
            method: "GET",
            secured: true,
            types: [
                reports.requestReportsShow,
                reports.reportsShowSuccess,
                reports.reportsShowFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting reports show request with id " + id);
        return apiRequest(request);
    }

    /**
     * Get the action to perform a reports update request.
     *
     * @return Action<ApiRequest>
     */
    // TODO actually pass through the report to store.
    public static reportsUpdate(id: number): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "reports/" + id,
            method: "PUT",
            secured: true,
            types: [
                reports.requestReportsUpdate,
                reports.reportsUpdateSuccess,
                reports.reportsUpdateFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting reports update request with id " + id);
        return apiRequest(request);
    }

    /**
     * Get the action to perform a reports destroy request.
     *
     * @param id number
     * @return Action<ApiRequest>
     */
    public static reportsDestroy(id: number): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "reports/" + id,
            method: "DELETE",
            secured: true,
            types: [
                reports.requestReportsDestroy,
                reports.reportsDestroySuccess,
                reports.reportsDestroyFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting reports destroy request with id " + id);
        return apiRequest(request);
    }

    // Users
    /**
     * Get the action to perform a users index request.
     *
     * @return Action<ApiRequest>
     */
    public static usersIndex(): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "users",
            method: "GET",
            secured: true,
            types: [
                auth.requestUsersIndex,
                auth.usersIndexSuccess,
                auth.usersIndexFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting users index request");
        return apiRequest(request);
    }

    /**
     * Get the action to perform a users store request.
     *
     * @return Action<ApiRequest>
     */
    public static usersStore(user: IUser): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "users",
            method: "POST",
            secured: true,
            types: [
                auth.requestUsersStore,
                auth.usersStoreSuccess,
                auth.usersStoreFailure,
            ],
            body: user,
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting users store request");
        return apiRequest(request);
    }

    /**
     * Get the action to perform a users show request.
     *
     * @param id number
     * @return Action<ApiRequest>
     */
    public static usersShow(id: number): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "users/" + id,
            method: "GET",
            secured: true,
            types: [
                auth.requestUsersShow,
                auth.usersShowSuccess,
                auth.usersShowFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting users show request with id " + id);
        return apiRequest(request);
    }

    /**
     * Get the action to perform a users update request.
     *
     * @return Action<ApiRequest>
     */
    public static usersUpdate(id: number, user: IUser): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "users/" + id,
            method: "PUT",
            secured: true,
            types: [
                auth.requestUsersUpdate,
                auth.usersUpdateSuccess,
                auth.usersUpdateFailure,
            ],
            body: user,
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting users update request with id " + id);
        return apiRequest(request);
    }

    /**
     * Get the action to perform a users destroy request.
     *
     * @param id number
     * @return Action<ApiRequest>
     */
    public static usersDestroy(id: number): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "users/" + id,
            method: "DELETE",
            secured: true,
            types: [
                auth.requestUsersDestroy,
                auth.usersDestroySuccess,
                auth.usersDestroyFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting users destroy request with id " + id);
        return apiRequest(request);
    }

    // Organisations
    /**
     * Get the action to perform a organisations index request.
     *
     * @return Action<ApiRequest>
     */
    public static organisationsIndex(): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "organisations",
            method: "GET",
            secured: true,
            types: [
                auth.requestOrganisationsIndex,
                auth.organisationsIndexSuccess,
                auth.organisationsIndexFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting organisations index request");
        return apiRequest(request);
    }

    // Roles
    /**
     * Get the action to perform a roles index request.
     *
     * @return Action<ApiRequest>
     */
    public static rolesIndex(): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "roles",
            method: "GET",
            secured: true,
            types: [
                auth.requestRolesIndex,
                auth.rolesIndexSuccess,
                auth.rolesIndexFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting roles index request");
        return apiRequest(request);
    }

    // Albums
    /**
     * Get the action to perform an albums index request.
     *
     * @return Action<ApiRequest>
     */
    public static albumsIndex(page: number = 1): Action<ApiRequest> {
        const queryStrings: string[] = [];
        if (page > 1) {
            queryStrings.push("page=" + page);
        }
        const queryString = (queryStrings.length > 0 ? "?" : "") + queryStrings.join("&");
        const options: ApiOptions = {
            endpoint: "albums" + queryString,
            method: "GET",
            secured: true,
            types: [
                music.requestAlbumsIndex,
                music.albumsIndexSuccess,
                music.albumsIndexFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting albums index request, page " + page);
        return apiRequest(request);
    }

    /**
     * Get the action to perform an albums store request.
     *
     * @return Action<ApiRequest>
     */
    // TODO actually pass through the album to store.
    public static albumsStore(): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "albums",
            method: "POST",
            secured: true,
            types: [
                music.requestAlbumsStore,
                music.albumsStoreSuccess,
                music.albumsStoreFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting albums store request");
        return apiRequest(request);
    }

    /**
     * Get the action to perform an albums show request.
     *
     * @param id number
     * @return Action<ApiRequest>
     */
    public static albumsShow(id: number): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "albums/" + id,
            method: "GET",
            secured: true,
            types: [
                music.requestAlbumsShow,
                music.albumsShowSuccess,
                music.albumsShowFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting albums show request with id " + id);
        return apiRequest(request);
    }

    /**
     * Get the action to perform an albums update request.
     *
     * @return Action<ApiRequest>
     */
    // TODO actually pass through the album to store.
    public static albumsUpdate(id: number): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "albums/" + id,
            method: "PUT",
            secured: true,
            types: [
                music.requestAlbumsUpdate,
                music.albumsUpdateSuccess,
                music.albumsUpdateFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting albums update request with id " + id);
        return apiRequest(request);
    }

    /**
     * Get the action to perform an albums destroy request.
     *
     * @param id number
     * @return Action<ApiRequest>
     */
    public static albumsDestroy(id: number): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "albums/" + id,
            method: "DELETE",
            secured: true,
            types: [
                music.requestAlbumsDestroy,
                music.albumsDestroySuccess,
                music.albumsDestroyFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting albums destroy request with id " + id);
        return apiRequest(request);
    }

    /**
     * Get the action to perform an albums search request.
     *
     * @param query string
     * @return Action<ApiRequest>
     */
    public static albumsSearch(query: string): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "albums/search?q=" + encodeURIComponent(query),
            method: "GET",
            secured: true,
            types: [
                music.requestAlbumsSearch,
                music.albumsSearchSuccess,
                music.albumsSearchFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting albums search request with query '" + query + "'");
        return apiRequest(request);
    }

    // Tracks
    /**
     * Get the action to perform an tracks index request.
     *
     * @return Action<ApiRequest>
     */
    public static tracksIndex(): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "tracks",
            method: "GET",
            secured: true,
            types: [
                music.requestTracksIndex,
                music.tracksIndexSuccess,
                music.tracksIndexFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting tracks index request");
        return apiRequest(request);
    }

    /**
     * Get the action to perform an tracks store request.
     *
     * @return Action<ApiRequest>
     */
    // TODO actually pass through the album to store.
    public static tracksStore(): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "tracks",
            method: "POST",
            secured: true,
            types: [
                music.requestTracksStore,
                music.tracksStoreSuccess,
                music.tracksStoreFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting tracks store request");
        return apiRequest(request);
    }

    /**
     * Get the action to perform an tracks show request.
     *
     * @param id number
     * @return Action<ApiRequest>
     */
    public static tracksShow(id: number): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "tracks/" + id,
            method: "GET",
            secured: true,
            types: [
                music.requestTracksShow,
                music.tracksShowSuccess,
                music.tracksShowFailure,
                music.tracksShowRedirect,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting tracks show request with id " + id);
        return apiRequest(request);
    }

    /**
     * Get the action to perform an tracks update request.
     *
     * @return Action<ApiRequest>
     */
    // TODO actually pass through the album to store.
    public static tracksUpdate(id: number): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "tracks/" + id,
            method: "PUT",
            secured: true,
            types: [
                music.requestTracksUpdate,
                music.tracksUpdateSuccess,
                music.tracksUpdateFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting tracks update request with id " + id);
        return apiRequest(request);
    }

    /**
     * Get the action to perform an tracks destroy request.
     *
     * @param id number
     * @return Action<ApiRequest>
     */
    public static tracksDestroy(id: number): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "tracks/" + id,
            method: "DELETE",
            secured: true,
            types: [
                music.requestTracksDestroy,
                music.tracksDestroySuccess,
                music.tracksDestroyFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting tracks destroy request with id " + id);
        return apiRequest(request);
    }

    /**
     * Get the action to perform an tracks search request.
     *
     * @param query string
     * @return Action<ApiRequest>
     */
    public static tracksSearch(query: string): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "tracks/search?q=" + encodeURIComponent(query),
            method: "GET",
            secured: true,
            types: [
                music.requestTracksSearch,
                music.tracksSearchSuccess,
                music.tracksSearchFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting tracks search request with query '" + query + "'");
        return apiRequest(request);
    }

    // Artists
    /**
     * Get the action to perform an artists show request.
     *
     * @param id number
     * @return Action<ApiRequest>
     */
    public static artistsShow(id: number): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "artists/" + id,
            method: "GET",
            secured: true,
            types: [
                music.requestArtistsShow,
                music.artistsShowSuccess,
                music.artistsShowFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting artists show request with id " + id);
        return apiRequest(request);
    }

    // Sales
    /**
     * Get the action to perform a sales get request.
     *
     * @return Action<ApiRequest>
     */
    public static salesGet(params: TrendsDataGetOptions = {}): Action<ApiRequest> {
        const { from, to, download, albumId, trackId, artistId } = defaultTrendsDataGetOptions(params);

        let types: Array<ActionCreator<any>> = [];
        if (!download) {
            types = [
                sales.requestSalesGet,
                sales.salesGetSuccess,
                sales.salesGetFailure,
            ];
        }
        const request = new ApiRequest({
            options: {
                endpoint: buildDatePeriodUrl("sales", { from, to, download, albumId, trackId, artistId }),
                method: "GET",
                secured: true,
                file: download,
                types,
            },
        });
        log.info("Attempting sales " + (download ? "download" : "get") + " request");
        return apiRequest(request);
    }

    // Demographics
    /**
     * Get the action to perform a demographics get request.
     *
     * @return Action<ApiRequest>
     */
    public static demographicsGet(params: DemographicsGetOptions = {}): Action<ApiRequest> {
        const { from, to, download, albumId, trackId, artistId } = defaultTrendsDataGetOptions(params);

        let types: Array<ActionCreator<any>> = [];
        if (!download) {
            types = [
                sales.requestDemographicsGet,
                sales.demographicsGetSuccess,
                sales.demographicsGetFailure,
            ];
        }
        let url = "demographics";
        if (params.type === "gender") {
            url += "/gender";
        }
        else if (params.type === "age") {
            url += "/age";
        }
        else if (params.type === "region") {
            url += "/region";
        }
        const request = new ApiRequest({
            options: {
                endpoint: buildDatePeriodUrl(url, { from, to, download, albumId, trackId, artistId }),
                method: "GET",
                secured: true,
                file: download,
                types,
            },
        });
        log.info("Attempting " + url + " " + (download ? "download" : "get") + " request");
        return apiRequest(request);
    }

    // Headline stats
    /**
     * Get the action to perform a sales get request.
     *
     * @return Action<ApiRequest>
     */
    public static headlineStatsGet(params: TrendsDataGetOptions = {}): Action<ApiRequest> {
        const { from, to, download, albumId, trackId, artistId } = defaultTrendsDataGetOptions(params);

        let types: Array<ActionCreator<any>> = [];
        if (!download) {
            types = [
                sales.requestHeadlineStatsGet,
                sales.headlineStatsGetSuccess,
                sales.headlineStatsGetFailure,
            ];
        }
        const request = new ApiRequest({
            options: {
                endpoint: buildDatePeriodUrl("headline-stats", { from, to, download, albumId, trackId, artistId }),
                method: "GET",
                secured: true,
                file: download,
                types,
            },
        });
        log.info("Attempting headline stats " + (download ? "download" : "get") + " request");
        return apiRequest(request);
    }

    // Search
    /**
     * Get the action to perform a search request.
     *
     * @return Action<ApiRequest>
     */
    public static search(query: string): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "search?q=" + encodeURIComponent(query),
            method: "GET",
            secured: true,
            types: [
                search.requestSearch,
                search.searchSuccess,
                search.searchFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting search request");
        return apiRequest(request);
    }

    // Deliveries
    /**
     * Get the action to perform a deliveries index request.
     *
     * @return Action<ApiRequest>
     */
    public static deliveriesIndex(page: number = 1, filters: {
        albumIds?: number[],
        dspIds?: number[],
        statuses?: string[],
        types?: string[],
    } = {}): Action<ApiRequest> {
        const queryStrings: string[] = [];
        if (page > 1) {
            queryStrings.push("page=" + page);
        }
        if (filters.albumIds != null) {
            for (const albumId of filters.albumIds) {
                queryStrings.push("album_ids[]=" + albumId);
            }
        }
        if (filters.dspIds != null) {
            for (const dspId of filters.dspIds) {
                queryStrings.push("dsp_ids[]=" + dspId);
            }
        }
        if (filters.statuses != null) {
            for (const status of filters.statuses) {
                queryStrings.push("statuses[]=" + status);
            }
        }
        if (filters.types != null) {
            for (const type of filters.types) {
                queryStrings.push("types[]=" + type);
            }
        }
        const queryString = (queryStrings.length > 0 ? "?" : "") + queryStrings.join("&");
        const options: ApiOptions = {
            endpoint: "deliveries" + queryString,
            method: "GET",
            secured: true,
            types: [
                deliveries.requestDeliveriesIndex,
                deliveries.deliveriesIndexSuccess,
                deliveries.deliveriesIndexFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting deliveries index request, page " + page);
        return apiRequest(request);
    }

    /**
     * Get the action to perform an deliveries show request.
     *
     * @param id number
     * @return Action<ApiRequest>
     */
    public static deliveriesShow(id: string): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "deliveries/" + id,
            method: "GET",
            secured: true,
            types: [
                deliveries.requestDeliveriesShow,
                deliveries.deliveriesShowSuccess,
                deliveries.deliveriesShowFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting deliveries show request with id " + id);
        return apiRequest(request);
    }

    // Bundles
    /**
     * Get the action to perform a bundles index request.
     *
     * @return Action<ApiRequest>
     */
    public static bundlesIndex(page: number = 1): Action<ApiRequest> {
        const queryStrings: string[] = [];
        if (page > 1) {
            queryStrings.push("page=" + page);
        }
        const queryString = (queryStrings.length > 0 ? "?" : "") + queryStrings.join("&");
        const options: ApiOptions = {
            endpoint: "bundles" + queryString,
            method: "GET",
            secured: true,
            types: [
                upload.requestBundlesIndex,
                upload.bundlesIndexSuccess,
                upload.bundlesIndexFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting bundles index request, page " + page);
        return apiRequest(request);
    }

    /**
     * Get the action to perform a bundles store request.
     *
     * @return Action<ApiRequest>
     */
    public static bundlesStore(file: File): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "bundles",
            method: "POST",
            secured: true,
            types: [
                upload.requestBundlesStore,
                upload.bundlesStoreSuccess,
                upload.bundlesStoreFailure,
            ],
            body: file,
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting bundles store request");
        return apiRequest(request);
    }

    /**
     * Get the action to perform an bundles show request.
     *
     * @param id number
     * @return Action<ApiRequest>
     */
    public static bundlesShow(id: number): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "bundles/" + id,
            method: "GET",
            secured: true,
            types: [
                upload.requestBundlesShow,
                upload.bundlesShowSuccess,
                upload.bundlesShowFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting bundles show request with id " + id);
        return apiRequest(request);
    }

    /**
     * Get the action to perform an bundles update request.
     *
     * @param id number
     * @param File file
     * @return Action<ApiRequest>
     */
    public static bundlesUpdate(id: number, file: File): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "bundles/" + id,
            method: "PUT",
            secured: true,
            types: [
                upload.requestBundlesUpdate,
                upload.bundlesUpdateSuccess,
                upload.bundlesUpdateFailure,
            ],
            body: file,
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting bundles update request with id " + id);
        return apiRequest(request);
    }

    /**
     * Get the action to perform a bundles store album request.
     *
     * @return Action<ApiRequest>
     */
    public static bundlesStoreAlbum(id: number): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "bundles/" + id + "/album",
            uri: "bundles/{id}/album",
            method: "POST",
            secured: true,
            types: [
                upload.requestBundlesStoreAlbum,
                upload.bundlesStoreAlbumSuccess,
                upload.bundlesStoreAlbumFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting bundles store album request with id " + id);
        return apiRequest(request);
    }

    /**
     * Get the action to perform a bundles upload zip request.
     *
     * @return Action<ApiRequest>
     */
    public static bundlesUploadZip(id: number, file: File): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "bundles/" + id + "/zip",
            uri: "bundles/{id}/zip",
            method: "POST",
            secured: true,
            types: [
                upload.requestBundlesUploadZip,
                upload.bundlesUploadZipSuccess,
                upload.bundlesUploadZipFailure,
            ],
            body: file,
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting bundles upload zip request with id " + id);
        return apiRequest(request);
    }

    /**
     * Get the action to perform a bundles complete request.
     *
     * @return Action<ApiRequest>
     */
    public static bundlesComplete(id: number): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "bundles/" + id + "/complete",
            uri: "bundles/{id}/complete",
            method: "POST",
            secured: true,
            types: [
                upload.requestBundlesComplete,
                upload.bundlesCompleteSuccess,
                upload.bundlesCompleteFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting bundles complete request with id " + id);
        return apiRequest(request);
    }

    // Playlists
    /**
     * Get the action to perform a playlists index request.
     *
     * @return Action<ApiRequest>
     */
    public static playlistsIndex(): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "playlists",
            method: "GET",
            secured: true,
            types: [
                vendor.requestPlaylistsIndex,
                vendor.playlistsIndexSuccess,
                vendor.playlistsIndexFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting playlists index request");
        return apiRequest(request);
    }

    /**
     * Get the action to perform an playlists show request.
     *
     * @param id number
     * @return Action<ApiRequest>
     */
    public static playlistsShow(id: number, params: DatePeriodGetOptions): Action<ApiRequest> {
        // Set the defaults
        const { from, to, download } = defaultDatePeriodGetOptions(params);

        const fromStr = moment(from).format("YYYY-MM-DD");
        const toStr = moment(to).format("YYYY-MM-DD");
        const options: ApiOptions = {
            endpoint: buildDatePeriodUrl("playlists/" + id, { from, to, download }),
            method: "GET",
            secured: true,
            types: [
                vendor.requestPlaylistsShow,
                vendor.playlistsShowSuccess,
                vendor.playlistsShowFailure,
            ],
            file: download,
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting playlists " + (download ? "download" : "show") +
            " request with id " + id + ", from " + fromStr + " to " + toStr);
        return apiRequest(request);
    }

    /**
     * Get the action to perform a playlists trends request.
     *
     * @return Action<ApiRequest>
     */
    public static playlistsTrends(params: PaginatedTrendsGetOptions = {}): Action<ApiRequest> {
        const { from, to, download, albumId, trackId, artistId } = defaultTrendsDataGetOptions(params);

        let types: Array<ActionCreator<any>> = [];
        if (!download) {
            types = [
                sales.requestPlaylistTrendsGet,
                sales.playlistTrendsGetSuccess,
                sales.playlistTrendsGetFailure,
            ];
        }
        const request = new ApiRequest({
            options: {
                endpoint: buildDatePeriodUrl(
                    "playlists/trends", {
                        from,
                        to,
                        download,
                        albumId,
                        trackId,
                        artistId,
                        page: params.page,
                    } as PaginatedTrendsGetOptions),
                method: "GET",
                secured: true,
                file: download,
                types,
            },
        });
        log.info("Attempting playlist trends " + (download ? "download" : "get") + " request");
        return apiRequest(request);
    }

    // Vendor tracks
    /**
     * Get the action to perform a vendor tracks index request.
     *
     * @return Action<ApiRequest>
     */
    public static vendorTracksIndex(): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "vendor/tracks",
            method: "GET",
            secured: true,
            types: [
                vendor.requestTracksIndex,
                vendor.tracksIndexSuccess,
                vendor.tracksIndexFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting vendor tracks index request");
        return apiRequest(request);
    }

    /**
     * Get the action to perform a vendor tracks index download request.
     *
     * @return Action<ApiRequest>
     */
    public static vendorTracksIndexDownload(): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "vendor/tracks/download",
            method: "GET",
            secured: true,
            types: [],
            file: true,
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting vendor tracks index download request");
        return apiRequest(request);
    }

    // Dsps
    /**
     * Get the action to perform a dsps index request.
     *
     * @return Action<ApiRequest>
     */
    public static dspsIndex(): Action<ApiRequest> {
        const options: ApiOptions = {
            endpoint: "dsps",
            method: "GET",
            secured: true,
            types: [
                dsps.requestDspsIndex,
                dsps.dspsIndexSuccess,
                dsps.dspsIndexFailure,
            ],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting dsps index request");
        return apiRequest(request);
    }

    // Charts
    /**
     * Get the action to perform a charts index request.
     *
     * @return Action<ApiRequest>
     */
    public static chartsIndex(params: ChartsDataGetOptions = {}): Action<ApiRequest> {
        const { from, to, download, name } = defaultChartsDataGetOptions(params);

        const options: ApiOptions = {
            endpoint: buildDatePeriodUrl("charts", { from, to, download, name }),
            method: "GET",
            secured: true,
            types: [
                charts.requestChartsIndex,
                charts.chartsIndexSuccess,
                charts.chartsIndexFailure,
            ],
            file: download || undefined,
        };
        const request = new ApiRequest({
            options,
        });
        log.info(
            "Attempting charts "
                + (download ? "download" : "index")
                + (name ? " '" + name + "'" : "")
                + " request",
        );
        return apiRequest(request);
    }

    /**
     * Get the action to perform a reports show request.
     *
     * @param string url
     * @return Action<ApiRequest>
     */
    public static downloadFile(url: string): Action<ApiRequest> {
        // Trim the front of the url
        url = trimStart(url, "/");
        const options: ApiOptions = {
            endpoint: url,
            method: "GET",
            secured: true,
            file: true,
            types: [],
        };
        const request = new ApiRequest({
            options,
        });
        log.info("Attempting file download for url '" + url + "'");
        return apiRequest(request);
    }
}
